import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API_URL } from '../config/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AmazonDisclosure } from '../components/AmazonDisclosure';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import {
  Drop,
  Thermometer,
  TestTube,
  FlowerTulip,
  CloudSun,
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  Sparkle,
  Package,
  Handshake,
} from '@phosphor-icons/react';

/**
 * TurfPulse Lab Starter Kit — public affiliate landing page.
 *
 * Purpose: concentrate intent-to-buy traffic on one page where the products
 * directly map to features users already want in the app. Every product link
 * is auto-tagged server-side via utils/amazon.tag_all_amazon_links.
 */

// ------------------------------------------------------------------
// Done-for-you subscription boxes (Awin-tagged where we have approval).
// awinmid=88419 (DoMyOwn) · awinaffid=2806920 (Hi Tex Lawn Pro LLC)
// Editorial rule: positive framing only. No ranking, no FUD, no quality
// claims about products we haven't personally tested at scale.
// ------------------------------------------------------------------
const SUBSCRIPTION_BOXES = [
  {
    name: "DoMyOwn's Lawn Box",
    tagline: 'A real retailer\'s subscription pack',
    why: 'Backed by an established lawn-care retailer (1M+ products in catalog). Curated seasonal product packs for your lawn type and region. Recurring shipments timed to your grass cycle.',
    fit: 'Best if you already trust DoMyOwn for one-off pro-grade orders and want the convenience of a recurring schedule.',
    url: 'https://www.awin1.com/cread.php?awinmid=88419&awinaffid=2806920&ued=' +
      encodeURIComponent('https://www.domyown.com/subscriptions/lawn-box-program'),
    cta: 'See the Lawn Box',
    sponsored: true,
    testid: 'box-domyown-lawnbox',
  },
  {
    name: 'Sunday Lawn Care',
    tagline: 'Bio-based, soil-test-driven',
    why: 'Pioneered the soil-test-into-pouches model. Bio-based nutrient inputs (seaweed, molasses, kelp). Seasonal nutrient pouches mailed directly to you.',
    fit: 'Best if "as natural as practical" is a top priority and you want a single-decision experience.',
    url: 'https://www.getsunday.com/',
    cta: 'Visit Sunday',
    sponsored: false,
    testid: 'box-sunday',
  },
  {
    name: 'Lawnbright',
    tagline: 'All-natural, pet- and kid-safe focus',
    why: 'All-natural product line emphasizing pet-safe, kid-safe formulations. Custom plans based on a starter soil test.',
    fit: 'Best if you have pets, young kids, or strong organic preferences and want zero synthetic inputs.',
    url: 'https://www.lawnbright.com/',
    cta: 'Visit Lawnbright',
    sponsored: false,
    testid: 'box-lawnbright',
  },
];

const TIER_BADGE = {
  foundation: { label: 'Foundation', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30' },
  enthusiast: { label: 'Upgrade', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30' },
};

const PAIRS_ICON = {
  ecowitt: <Drop size={16} weight="fill" />,
  'soil-test': <TestTube size={16} weight="fill" />,
  irrigation: <CloudSun size={16} weight="fill" />,
};

const PAIRS_HREF = {
  ecowitt: '/app',
  'soil-test': '/soil-test',
  irrigation: '/irrigation',
};

const PAIRS_LABEL = {
  ecowitt: 'Pairs with Ecowitt dashboard tile',
  'soil-test': 'Upload the PDF to /soil-test',
  irrigation: 'Works with /irrigation recommender',
};

const KitCard = ({ item }) => {
  const tier = TIER_BADGE[item.tier] || TIER_BADGE.foundation;
  return (
    <Card
      data-testid={`kit-item-${item.slug}`}
      className="group relative overflow-hidden border-border/60 hover:border-primary/40 transition-colors"
    >
      <CardContent className="p-6 flex flex-col h-full">
        {/* Header row: tier + price band */}
        <div className="flex items-center justify-between mb-3">
          <span
            className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${tier.cls}`}
          >
            {tier.label}
          </span>
          <span className="text-xs font-mono text-muted-foreground">{item.price_band}</span>
        </div>

        {/* Name + tagline */}
        <h3 className="text-lg font-semibold leading-snug">{item.name}</h3>
        <p className="text-sm text-primary/90 font-medium mt-1 italic">
          {item.tagline}
        </p>

        {/* Why */}
        <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">
          {item.why}
        </p>

        {/* Pairs-with chip */}
        {item.pairs_with && PAIRS_HREF[item.pairs_with] && (
          <Link
            to={PAIRS_HREF[item.pairs_with]}
            className="inline-flex items-center gap-1.5 mt-4 text-xs font-medium text-primary hover:underline"
            data-testid={`kit-pairs-${item.slug}`}
          >
            {PAIRS_ICON[item.pairs_with]}
            {PAIRS_LABEL[item.pairs_with]}
            <ArrowRight size={12} />
          </Link>
        )}

        {/* CTA */}
        <Button
          asChild
          className="mt-5 w-full"
          data-testid={`kit-buy-${item.slug}`}
        >
          <a
            href={item.amazon_url}
            target="_blank"
            rel="nofollow sponsored noopener noreferrer"
          >
            Shop on Amazon
            <ArrowUpRight size={16} className="ml-1" />
          </a>
        </Button>
      </CardContent>
    </Card>
  );
};

export const KitPage = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/kit`)
      .then((r) => setData(r.data))
      .catch(() => setErr('Could not load the starter kit. Refresh in a moment.'));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="kit-page">
      <SEO
        title="TurfPulse Lab Starter Kit — soil probe, moisture meter, catch cups"
        description="The gear we actually use on our own lawns. Soil probe, tuning-fork moisture meter, catch-cup set, Ecowitt sensors. Honest picks, affiliate-disclosed, no filler."
        keywords="lawn care starter kit, soil probe, soil moisture meter, catch cup set, Ecowitt soil moisture, lawn enthusiast gear"
        path="/kit"
      />
      <BreadcrumbJsonLd items={[
        { label: 'Home', path: '/' },
        { label: 'Starter Kit', path: '/kit' },
      ]} />
      {/* Hero */}
      <section className="border-b border-border/60 bg-gradient-to-b from-primary/5 to-transparent">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary mb-4">
            <Sparkle size={14} weight="fill" /> TurfPulse Lab
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
            data-testid="kit-hero-title"
          >
            The Starter Kit for
            <br />
            <span className="text-primary">people who actually care</span>
            <br />
            about their lawn.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Hand-picked hardware that turns TurfPulse from a recommendation engine into
            a feedback loop. Nothing here is filler. Every item pairs with a feature we already build.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/app">
              <Button size="lg" data-testid="kit-cta-dashboard">
                Back to dashboard
                <ArrowRight size={16} className="ml-1.5" />
              </Button>
            </Link>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck size={14} weight="fill" />
              Editorial picks. No pay-to-place.
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14">
        {err && (
          <div className="text-center text-sm text-destructive mb-6">{err}</div>
        )}
        {!data && !err && (
          <div className="text-center text-sm text-muted-foreground">Loading kit…</div>
        )}
        {data && (
          <>
            <div className="mb-8">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <FlowerTulip size={14} weight="fill" /> The Lab Kit
              </div>
              <h2 className="text-2xl font-bold mt-1">
                Measure. Analyze. Act.
              </h2>
              {/* FTC-required disclosure positioned ABOVE the affiliate grid */}
              <div className="mt-4 rounded-lg bg-muted/40 border border-border/60 px-4 py-3">
                <AmazonDisclosure variant="inline" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.items.map((item) => (
                <KitCard key={item.slug} item={item} />
              ))}
            </div>

            {/* How the kit flows */}
            <div className="mt-14 rounded-xl border border-border/60 bg-card p-6 sm:p-8">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-primary mb-3">
                <Thermometer size={14} weight="fill" /> How the loop works
              </div>
              <ol className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                <li>
                  <span className="text-foreground font-semibold">1. Measure.</span>{' '}
                  WH51 probes tell TurfPulse what your soil is actually doing. WH34 pins down soil temperature so
                  pre-emergent windows aren't guesswork.
                </li>
                <li>
                  <span className="text-foreground font-semibold">2. Analyze.</span>{' '}
                  A MySoil test gives us your N-P-K and pH. Upload the PDF; we parse it into a fertilizer plan.
                </li>
                <li>
                  <span className="text-foreground font-semibold">3. Act.</span>{' '}
                  The B-hyve timer executes the irrigation plan TurfPulse generates — automatic skip on rain,
                  cycle-and-soak to kill runoff.
                </li>
              </ol>
            </div>

            {/* ───────────────── Done-for-you Lawn Care ───────────────── */}
            <div className="mt-14" data-testid="kit-boxes-section">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Package size={14} weight="fill" /> Done-for-you Lawn Care
              </div>
              <h2 className="text-2xl font-bold mt-1">
                Or just have someone else ship the products.
              </h2>
              <p className="mt-3 text-sm text-muted-foreground max-w-2xl leading-relaxed">
                If sourcing your own bag of pre-emergent and a quart of PGR isn't your thing —
                that's totally fine. Several subscription services do the product part for you.
                <strong className="text-foreground"> TurfPulse still tells you when to apply
                whatever shows up at your door.</strong>
              </p>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
                {SUBSCRIPTION_BOXES.map((box) => (
                  <Card
                    key={box.testid}
                    data-testid={box.testid}
                    className="border-border/60 hover:border-primary/40 transition-colors flex flex-col"
                  >
                    <CardContent className="p-6 flex flex-col h-full">
                      <h3 className="text-lg font-semibold leading-snug">{box.name}</h3>
                      <p className="text-sm text-primary/90 font-medium mt-1 italic">
                        {box.tagline}
                      </p>
                      <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                        {box.why}
                      </p>
                      <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                        <span className="text-foreground font-semibold">Best for: </span>
                        {box.fit}
                      </p>
                      <Button
                        asChild
                        variant={box.sponsored ? 'default' : 'outline'}
                        className="mt-5 w-full"
                        data-testid={`${box.testid}-cta`}
                      >
                        <a
                          href={box.url}
                          target="_blank"
                          rel={box.sponsored ? 'sponsored noopener noreferrer' : 'noopener noreferrer'}
                        >
                          {box.cta}
                          <ArrowUpRight size={16} className="ml-1" />
                        </a>
                      </Button>
                      {box.sponsored && (
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-2 text-center">
                          Affiliate link · we may earn a commission
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="mt-6 rounded-lg border border-border/60 bg-muted/30 px-4 py-3 flex items-start gap-3">
                <Handshake size={20} weight="duotone" className="text-primary mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  <span className="text-foreground font-semibold">Editorial note:</span>{' '}
                  These are listed for category awareness, not ranked. We don't claim one is
                  better than another — they target slightly different priorities. We currently
                  earn a commission only on the DoMyOwn link (Awin-disclosed). Sunday and Lawnbright
                  are linked direct to their sites with no affiliate relationship.
                </p>
              </div>
            </div>
            {/* ─────────────── /Done-for-you Lawn Care ─────────────── */}

            {/* FTC / Amazon Associates full footer disclosure */}
            <AmazonDisclosure />

            {/* Editorial/backend disclosure also present for context */}
            <p
              className="text-xs text-muted-foreground mt-4 leading-relaxed"
              data-testid="kit-disclosure"
            >
              {data.disclosure}
            </p>
          </>
        )}
      </section>
    </div>
  );
};

export default KitPage;
